/**
 * PC desktop information architecture.
 *
 * Layers:
 * 1) BM_* atomic capabilities (permission / bmobile catalog / route)
 * 2) PC_* workspaces (desktop icons, dock, in-app top navigation)
 * 3) desktopGroup (home page sections: 作业 / 经营 / 配置 / 治理)
 *
 * Rules:
 * - Daily ops and setup must not share the same first-class icon when possible
 * - Workspace top nav is flat (all items shown; overflow scrolls horizontally)
 * - Catalog category names on BM_* align with user-facing groups (not free-form)
 */

export const PC_DESKTOP_GROUPS = Object.freeze([
	{ id: 'ops', name: '作业', description: '门店当天要完成的事' },
	{ id: 'grow', name: '经营', description: '会员增长与口碑' },
	{ id: 'setup', name: '配置', description: '人货场与规则设置' },
	{ id: 'govern', name: '治理', description: '组织、权限与系统' }
])

export const PC_APP_REGISTRY = [
	{
		appCode: 'BM_MEMBER',
		name: '会员管理',
		route: '/biz/mrmember',
		routeCapabilityCodes: ['BM_MEMBER'],
		icon: '/app-icons/member-management.webp',
		category: '作业'
	},
	{
		appCode: 'BM_ATTENDANCE',
		name: '考勤打卡',
		route: '/biz/mrclockrecords',
		routeCapabilityCodes: ['attendance.clock_record.view'],
		icon: '/app-icons/attendance.webp',
		category: '作业'
	},
	{
		appCode: 'BM_ATTENDANCE_CONFIG',
		name: '考勤规则',
		route: '/biz/mrstoreattendanceconfig',
		routeCapabilityCodes: ['BM_ATTENDANCE_CONFIG'],
		icon: '/app-icons/attendance-rules.webp',
		category: '配置'
	},
	{
		appCode: 'BM_STAFF_MANAGEMENT',
		name: '员工档案',
		route: '/biz/mrstorestaff',
		routeCapabilityCodes: ['BM_STAFF_MANAGEMENT', 'staff.access.manage'],
		icon: '/app-icons/staff-management.webp',
		category: '作业'
	},
	{
		appCode: 'BM_PARTTIME_STAFF',
		name: '兼职人员',
		route: '/biz/mrparttimeststaff',
		routeCapabilityCodes: ['BM_STAFF_MANAGEMENT'],
		icon: '/app-icons/staff-management.webp',
		category: '作业'
	},
	{
		appCode: 'BM_PARTTIME_PAYOUT',
		name: '兼职应付',
		route: '/biz/mrparttimepayout',
		routeCapabilityCodes: ['BM_STAFF_MANAGEMENT'],
		icon: '/app-icons/staff-management.webp',
		category: '作业'
	},
	{
		appCode: 'BM_ROOM_MANAGEMENT',
		name: '预约开单',
		route: '/biz/mrfrontdesk',
		routeCapabilityCodes: ['frontdesk.workbench.view'],
		icon: '/app-icons/room-management.webp',
		category: '作业'
	},
	{
		appCode: 'BM_LOTTERY',
		name: '抽奖活动',
		route: '/biz/luckyconfig',
		routeCapabilityCodes: ['BM_LOTTERY'],
		icon: '/app-icons/lottery.webp',
		category: '经营',
		scope: 'organization'
	},
	{
		appCode: 'BM_SERVICE_CATALOG',
		name: '服务项目',
		route: '/biz/mrspaproduct',
		routeCapabilityCodes: ['spa.product.manage'],
		icon: '/app-icons/service-catalog.webp',
		category: '配置'
	},
	{
		appCode: 'BM_GOODS',
		name: '商城商品',
		route: '/biz/mrgoods',
		routeCapabilityCodes: ['goods.catalog.view'],
		icon: '/app-icons/mall-goods.webp',
		category: '配置'
	},
	{
		appCode: 'BM_PROMOTION',
		name: '优惠活动',
		route: '/biz/promotiondate',
		routeCapabilityCodes: ['BM_PROMOTION'],
		icon: '/app-icons/promotions.webp',
		category: '经营'
	},
	{
		appCode: 'BM_TICKET_PACKAGE',
		name: '次卡套餐',
		route: '/biz/mrticketpackage',
		routeCapabilityCodes: ['BM_TICKET_PACKAGE'],
		icon: '/app-icons/ticket-packages.webp',
		category: '配置'
	},
	{
		appCode: 'BM_RECHARGE_PACKAGE',
		name: '充值套餐',
		route: '/biz/mrmembercardproduct',
		routeCapabilityCodes: ['BM_RECHARGE_PACKAGE'],
		icon: '/app-icons/recharge-packages.webp',
		category: '配置'
	},
	{
		appCode: 'BM_GIFT_RECHARGE',
		name: '礼品卡配置',
		route: '/biz/mrgiftrecharge',
		routeCapabilityCodes: ['BM_GIFT_RECHARGE'],
		icon: '/app-icons/gift-recharge.webp',
		category: '配置'
	},
	{
		appCode: 'BM_MANUAL_RECHARGE',
		name: '会员充值',
		route: '/biz/mrrechargeorder',
		routeCapabilityCodes: ['BM_RECHARGE_PACKAGE'],
		icon: '/app-icons/customer-recharge.webp',
		category: '作业'
	},
	{
		appCode: 'BM_TICKET_PACKAGE_PURCHASE',
		name: '次卡购买',
		route: '/biz/mrticketpackagepurchase',
		routeCapabilityCodes: ['BM_TICKET_PACKAGE_PURCHASE'],
		icon: '/app-icons/ticket-purchase.webp',
		category: '作业'
	},
	{
		appCode: 'BM_TICKET_BUNDLE_PURCHASE',
		name: '套票购买',
		route: '/biz/mrticketbundlepurchase',
		routeCapabilityCodes: ['BM_TICKET_BUNDLE_PURCHASE'],
		icon: '/app-icons/bundle-purchase.webp',
		category: '作业'
	},
	{
		appCode: 'BM_GIFT_CARD_PURCHASE',
		name: '礼品卡购买',
		route: '/biz/mrgiftcardpurchase',
		routeCapabilityCodes: ['BM_GIFT_CARD_PURCHASE'],
		icon: '/app-icons/gift-card-purchase.webp',
		category: '作业'
	},
	{
		appCode: 'BM_LOTTERY_VERIFY',
		name: '抽奖核销',
		route: '/biz/mrluckyrecord',
		routeCapabilityCodes: ['BM_LOTTERY_VERIFY', 'BM_LOTTERY'],
		icon: '/app-icons/lottery-redemption.webp',
		category: '经营'
	},
	{
		appCode: 'BM_CUSTOMER_SERVICE',
		name: '在线客服',
		route: '/biz/customer-service',
		routeCapabilityCodes: ['BM_CUSTOMER_SERVICE'],
		icon: '/app-icons/customer-service.webp',
		category: '治理',
		scope: 'organization'
	},
	{
		appCode: 'BM_SERVICE_ORDER',
		name: '服务开单',
		route: '/biz/booking-workbench',
		routeCapabilityCodes: ['BM_SERVICE_ORDER'],
		icon: '/app-icons/service-orders.webp',
		category: '作业'
	},
	{
		appCode: 'BM_APP_MANAGEMENT',
		name: '应用管理',
		route: '/biz/appmanagement',
		routeCapabilityCodes: ['app.catalog.view'],
		icon: '/app-icons/application-management.webp',
		category: '治理',
		scope: 'organization'
	},
	{
		appCode: 'BM_OA',
		name: '审批中心',
		route: '/biz/oa/records',
		routeCapabilityCodes: ['BM_OA'],
		icon: '/app-icons/attendance.webp',
		category: '治理'
	},
	{
		appCode: 'BM_OA_CONFIG',
		name: '审批配置',
		route: '/biz/oa/process-binding',
		routeCapabilityCodes: ['BM_OA_CONFIG'],
		icon: '/app-icons/attendance-rules.webp',
		category: '治理'
	}
]

export const BMOBILE_APP_CODES = Object.freeze([
	'BM_MEMBER',
	'BM_ATTENDANCE',
	'BM_ATTENDANCE_CONFIG',
	'BM_STAFF_MANAGEMENT',
	'BM_ROOM_MANAGEMENT',
	'BM_LOTTERY',
	'BM_SERVICE_CATALOG',
	'BM_GOODS',
	'BM_PROMOTION',
	'BM_TICKET_PACKAGE',
	'BM_RECHARGE_PACKAGE',
	'BM_GIFT_RECHARGE',
	'BM_MANUAL_RECHARGE',
	'BM_TICKET_PACKAGE_PURCHASE',
	'BM_TICKET_BUNDLE_PURCHASE',
	'BM_GIFT_CARD_PURCHASE',
	'BM_LOTTERY_VERIFY',
	'BM_CUSTOMER_SERVICE',
	'BM_SERVICE_ORDER',
	'BM_APP_MANAGEMENT',
	'BM_OA',
	'BM_OA_CONFIG'
])

const CORE_PC_APP_CODES = new Set(['BM_APP_MANAGEMENT'])
// Platform operations is a fixed control entry and is not controlled by the app catalogue.
const CORE_PC_WORKSPACE_CODES = new Set(['PC_SYSTEM', 'PC_PLATFORM_OPERATIONS'])
const PLATFORM_OPERATIONS_CAPABILITY = 'PLATFORM.OPS.MANAGE'

/**
 * Desktop workspaces.
 * desktopGroup + desktopOrder drive home page sections.
 * preferred dock uses preferredCore workspace codes in the shell.
 */
export const PC_WORKSPACE_REGISTRY = Object.freeze([
{
			appCode: 'PC_STORE_OPERATION',
			name: '超级工作台',
			icon: '/app-icons/pc-store-operation.webp',
			desktopGroup: 'ops',
			desktopOrder: 10,
			// 抽奖活动（组织级配置）+ 抽奖核销（门店作业）并入超级工作台顶部导航；结单查询紧跟预约开单
			appCodes: ['BM_ROOM_MANAGEMENT', 'BM_LOTTERY', 'BM_LOTTERY_VERIFY'],
			defaultRoute: '/biz/mrfrontdesk',
			defaultCapabilityCodes: ['frontdesk.workbench.view'],
			extraRoutesFirst: false,
		extraRoutes: [
			{
				name: '结单查询',
				route: '/biz/mrfrontdeskorders',
				capabilityCodes: ['frontdesk.workbench.view'],
				appCodes: ['BM_ROOM_MANAGEMENT']
			},
			{
				name: '会员中心',
				route: '/dashboard',
				capabilityCodes: ['common.workbench.access'],
				appCodes: ['BM_ROOM_MANAGEMENT']
			}
		]
	},
	{
		appCode: 'PC_ORDER_FULFILLMENT',
		name: '收银履约',
		icon: '/app-icons/pc-order-fulfillment.webp',
		desktopGroup: 'ops',
		desktopOrder: 20,
		// Purchase / open order first; documents after
		appCodes: ['BM_SERVICE_ORDER', 'BM_TICKET_PACKAGE_PURCHASE', 'BM_TICKET_BUNDLE_PURCHASE', 'BM_GIFT_CARD_PURCHASE'],
		extraRoutesFirst: false,
		extraRoutes: [
			{
				name: '服务工单',
				route: '/biz/mrserviceworkorder',
				capabilityCodes: ['BM_SERVICE_ORDER'],
				appCodes: ['BM_SERVICE_ORDER']
			},
			{
				name: '商品订单',
				route: '/biz/order-center',
				capabilityCodes: ['BM_SERVICE_ORDER'],
				appCodes: ['BM_SERVICE_ORDER']
			},
			{
				name: '次卡订单',
				route: '/biz/mrticketpackageorder',
				capabilityCodes: ['BM_TICKET_PACKAGE_PURCHASE'],
				appCodes: ['BM_TICKET_PACKAGE_PURCHASE']
			}
		]
	},
	{
		appCode: 'PC_MEMBER_OPERATION',
		name: '会员',
		icon: '/app-icons/pc-member-operation.webp',
		desktopGroup: 'ops',
		desktopOrder: 30,
		appCodes: ['BM_MEMBER', 'BM_MANUAL_RECHARGE'],
		extraRoutes: [
			{ name: '会员持卡', route: '/biz/mrmembercarditem', capabilityCodes: ['BM_MEMBER'], appCodes: ['BM_MEMBER'] },
			{ name: '积分记录', route: '/biz/mrpoints', capabilityCodes: ['BM_MEMBER'], appCodes: ['BM_MEMBER'] },
			{
				name: '开票记录',
				route: '/biz/mrinvoice',
				capabilityCodes: ['member.invoice.manage'],
				appCodes: ['BM_MEMBER']
			},
			{ name: '收货地址', route: '/biz/mruseraddress', capabilityCodes: ['BM_MEMBER'], appCodes: ['BM_MEMBER'] },
			{
				name: '礼品卡赠送',
				route: '/biz/mrgiftsendrecord',
				capabilityCodes: ['marketing.gift.record_view'],
				appCodes: ['BM_MEMBER']
			}
		]
	},
	{
		appCode: 'PC_STAFF',
		name: '人与排班',
		icon: '/app-icons/pc-staff.webp',
		desktopGroup: 'ops',
		desktopOrder: 40,
		appCodes: ['BM_STAFF_MANAGEMENT', 'BM_ATTENDANCE', 'BM_PARTTIME_STAFF', 'BM_PARTTIME_PAYOUT'],
		extraRoutes: [
			{
				name: '员工排班',
				route: '/biz/mrstaffshift',
				capabilityCodes: ['staff.shift.view'],
				appCodes: ['BM_ATTENDANCE']
			}
		]
	},
	{
		appCode: 'PC_STORE_PERFORMANCE',
		name: '门店业绩',
		icon: '/app-icons/pc-store-performance.webp',
		desktopGroup: 'grow',
		desktopOrder: 45,
		appCodes: [],
		defaultRoute: '/biz/storemoney',
		defaultCapabilityCodes: ['finance.store_money.view'],
		extraRoutes: [
			{
				name: '门店业绩',
				route: '/biz/storemoney',
				capabilityCodes: ['finance.store_money.view'],
				appCodes: ['BM_ROOM_MANAGEMENT']
			},
			{
				name: '业绩分析',
				route: '/biz/storemoneyanalysis',
				capabilityCodes: ['finance.store_money.view'],
				appCodes: ['BM_ROOM_MANAGEMENT']
			}
		]
	},
	{
		appCode: 'PC_MARKETING',
		name: '营销',
		icon: '/app-icons/pc-marketing.webp',
		desktopGroup: 'grow',
		desktopOrder: 50,
		// Workspace is store-level; only specific nav items are organization-scoped
		scope: 'store',
		appCodes: ['BM_PROMOTION'],
		extraRoutes: [
			{
				name: '达人预约',
				route: '/biz/daren',
				capabilityCodes: ['marketing.daren.manage'],
				appCodes: ['BM_PROMOTION'],
				scope: 'organization'
			},
			{ name: '用户评价', route: '/biz/mrreview', capabilityCodes: ['review.record.view'], appCodes: ['BM_PROMOTION'] },
			{
				name: '反馈管理',
				route: '/biz/mrfeedback',
				capabilityCodes: ['feedback.record.view'],
				appCodes: ['BM_PROMOTION'],
				scope: 'organization'
			},
			{
				name: '门店相册',
				route: '/biz/mralbumcategory',
				capabilityCodes: ['album.category.view'],
				appCodes: ['BM_PROMOTION']
			}
		]
	},
	{
		appCode: 'PC_CATALOG',
		name: '商品配置',
		icon: '/app-icons/pc-catalog.webp',
		desktopGroup: 'setup',
		desktopOrder: 60,
		appCodes: ['BM_SERVICE_CATALOG', 'BM_GOODS', 'BM_TICKET_PACKAGE', 'BM_RECHARGE_PACKAGE', 'BM_GIFT_RECHARGE']
	},
	{
		appCode: 'PC_HR',
		name: '人事薪酬',
		icon: '/app-icons/pc-hr.webp',
		desktopGroup: 'setup',
		desktopOrder: 70,
		// 任职管理已升级为门店人员管理中枢（人事薪酬主入口）；人与排班保留考勤与排班日常入口
		appCodes: ['BM_ATTENDANCE_CONFIG'],
		extraRoutes: [
			{
				name: '任职管理',
				route: '/biz/mrstaffassignment',
				capabilityCodes: ['staff.assignment.view'],
				appCodes: ['BM_STAFF_MANAGEMENT']
			},
			{
				name: '候选人',
				route: '/biz/mrstaffcandidate',
				capabilityCodes: ['staff.candidate.view'],
				appCodes: ['BM_STAFF_MANAGEMENT']
			},
			{
				name: '薪酬方案',
				route: '/biz/mrstaffcompensation',
				capabilityCodes: ['staff.compensation.view'],
				appCodes: ['BM_STAFF_MANAGEMENT']
			},
			{
				name: '工资单',
				route: '/biz/mrstaffpayroll',
				capabilityCodes: ['staff.payroll.view'],
				appCodes: ['BM_STAFF_MANAGEMENT']
			}
		]
	},
	{
		appCode: 'PC_OA',
		name: 'OA 协同',
		icon: '/app-icons/pc-oa.webp',
		desktopGroup: 'govern',
		desktopOrder: 80,
		appCodes: ['BM_OA', 'BM_OA_CONFIG']
	},
	{
		appCode: 'PC_CUSTOMER_SERVICE',
		name: '在线客服',
		icon: '/app-icons/pc-customer-service.webp',
		desktopGroup: 'govern',
		desktopOrder: 90,
		appCodes: ['BM_CUSTOMER_SERVICE']
	},
	{
		appCode: 'PC_PLATFORM_OPERATIONS',
		name: '平台运维',
		icon: '/app-icons/pc-platform-operations.webp',
		desktopGroup: 'govern',
		desktopOrder: 105,
		operatorOnly: true,
		scope: 'organization',
		appCodes: [],
		defaultRoute: '/platform/ops/config',
		defaultCapabilityCodes: [PLATFORM_OPERATIONS_CAPABILITY],
		extraRoutes: [
			{
				name: '基础配置',
				route: '/platform/ops/config',
				capabilityCodes: [PLATFORM_OPERATIONS_CAPABILITY],
				appCodes: ['BM_APP_MANAGEMENT'],
				operatorOnly: true
			},
			{
				name: '文件资产',
				route: '/platform/ops/files',
				capabilityCodes: [PLATFORM_OPERATIONS_CAPABILITY],
				appCodes: ['BM_APP_MANAGEMENT'],
				operatorOnly: true
			},
			{
				name: '邮件中心',
				route: '/platform/ops/email',
				capabilityCodes: [PLATFORM_OPERATIONS_CAPABILITY],
				appCodes: ['BM_APP_MANAGEMENT'],
				operatorOnly: true
			},
			{
				name: '站内消息',
				route: '/platform/ops/message',
				capabilityCodes: [PLATFORM_OPERATIONS_CAPABILITY],
				appCodes: ['BM_APP_MANAGEMENT'],
				operatorOnly: true
			},
			{
				name: '短信中心',
				route: '/platform/ops/sms',
				capabilityCodes: [PLATFORM_OPERATIONS_CAPABILITY],
				appCodes: ['BM_APP_MANAGEMENT'],
				operatorOnly: true
			},
			{
				name: '数据字典',
				route: '/platform/ops/dict',
				capabilityCodes: [PLATFORM_OPERATIONS_CAPABILITY],
				appCodes: ['BM_APP_MANAGEMENT'],
				operatorOnly: true
			},
			{
				name: '任务调度',
				route: '/platform/ops/jobs',
				capabilityCodes: [PLATFORM_OPERATIONS_CAPABILITY],
				appCodes: ['BM_APP_MANAGEMENT'],
				operatorOnly: true
			},
			{
				name: '访问日志',
				route: '/platform/ops/vislog',
				capabilityCodes: [PLATFORM_OPERATIONS_CAPABILITY],
				appCodes: ['BM_APP_MANAGEMENT'],
				operatorOnly: true
			},
			{
				name: '操作日志',
				route: '/platform/ops/oplog',
				capabilityCodes: [PLATFORM_OPERATIONS_CAPABILITY],
				appCodes: ['BM_APP_MANAGEMENT'],
				operatorOnly: true
			},
			{
				name: '系统监控',
				route: '/platform/ops/monitor',
				capabilityCodes: [PLATFORM_OPERATIONS_CAPABILITY],
				appCodes: ['BM_APP_MANAGEMENT'],
				operatorOnly: true
			},
			{
				name: '渠道配置',
				route: '/platform/ops/channels',
				capabilityCodes: [PLATFORM_OPERATIONS_CAPABILITY],
				appCodes: ['BM_APP_MANAGEMENT'],
				operatorOnly: true
			},
			{
				name: '三方用户',
				route: '/platform/ops/third-users',
				capabilityCodes: [PLATFORM_OPERATIONS_CAPABILITY],
				appCodes: ['BM_APP_MANAGEMENT'],
				operatorOnly: true
			},
			{
				name: '渠道绑定',
				route: '/platform/ops/channel-bindings',
				capabilityCodes: [PLATFORM_OPERATIONS_CAPABILITY],
				appCodes: ['BM_APP_MANAGEMENT'],
				operatorOnly: true
			},
			{
				name: '会话治理',
				route: '/platform/ops/sessions',
				capabilityCodes: [PLATFORM_OPERATIONS_CAPABILITY],
				appCodes: ['BM_APP_MANAGEMENT'],
				operatorOnly: true
			}
		]
	},
	{
		appCode: 'PC_SYSTEM',
		name: '系统设置',
		icon: '/app-icons/pc-system.webp',
		desktopGroup: 'govern',
		desktopOrder: 100,
		appCodes: ['BM_APP_MANAGEMENT'],
		scope: 'organization',
		extraRoutes: [
			{
				name: '门店管理',
				route: '/biz/mrstore',
				capabilityCodes: ['store.info.view'],
				appCodes: ['BM_APP_MANAGEMENT']
			},
			{ name: '机构管理', route: '/biz/org', capabilityCodes: ['org.structure.view'], appCodes: ['BM_APP_MANAGEMENT'] },
			{ name: '登录账号', route: '/biz/user', capabilityCodes: ['org.user.view'], appCodes: ['BM_APP_MANAGEMENT'] },
			{
				name: '岗位管理',
				route: '/biz/position',
				capabilityCodes: ['org.position.view'],
				appCodes: ['BM_APP_MANAGEMENT']
			},
			{
				name: 'AI 面相分析',
				route: '/biz/aifacereading',
				capabilityCodes: ['aiface.record.view'],
				appCodes: ['BM_APP_MANAGEMENT'],
				scope: 'organization'
			},
			{
				name: 'AI 知识库',
				route: '/biz/aifaceknowledge',
				capabilityCodes: ['aiface.knowledge.view'],
				appCodes: ['BM_APP_MANAGEMENT'],
				scope: 'organization'
			}
		]
	}
])

/** Dock fixed slots after 桌面 (order matters). Message center is separate UI. */
export const PC_DOCK_PREFERRED_CODES = Object.freeze([
	'PC_STORE_OPERATION',
	'PC_ORDER_FULFILLMENT',
	'PC_MEMBER_OPERATION',
	'PC_CUSTOMER_SERVICE',
	'PC_SYSTEM'
])

export function normalizePcRoutePath(route = '') {
	return String(route || '').split(/[?#]/, 1)[0]
}

export function pcRouteMatches(currentRoute = '', targetRoute = '') {
	const current = normalizePcRoutePath(currentRoute)
	const target = normalizePcRoutePath(targetRoute)
	return Boolean(target && (current === target || current.startsWith(`${target}/`)))
}

// Routes kept out of desktop navigation still need the same guard as their migrated SYS_RESOURCE menu.
const PC_NON_NAVIGATION_ROUTE_CAPABILITIES = Object.freeze({
	// Workflow approval is opened from notifications, so it is intentionally not a desktop tile.
	// FlwTaskController is guarded by BM_OA; the admin-only OA leaf does not grant task actions.
	'/flw/todoTask': ['BM_OA'],
	'/biz/mralbumimage': ['album.image.view'],
	'/biz/mrpromotiontime': ['BM_PROMOTION'],
	'/biz/mrreviewreply': ['review.reply.view'],
	'/biz/mrspaorder': ['order.goods.view']
})

const PC_UNGUARDED_ROUTES = Object.freeze(['/', '/index', '/userCenter'])

export function collectVisibleMenuPaths(menus = []) {
	const paths = new Set()
	const visit = (items) => {
		for (const item of items || []) {
			const hidden = item?.visible === 0 || item?.meta?.visible === 0 || item?.meta?.hidden
			if (!hidden && item?.path) paths.add(normalizePcRoutePath(item.path))
			if (!hidden && Array.isArray(item?.children)) visit(item.children)
		}
	}
	visit(menus)
	return paths
}

function collectEnabledCatalogAppCodes(catalog) {
	if (catalog === undefined) return null
	if (!Array.isArray(catalog)) return new Set()
	const codes = new Set()
	for (const category of catalog) {
		for (const app of category?.apps || []) {
			const code = String(app?.appCode || '')
				.trim()
				.toUpperCase()
			if (code) codes.add(code)
		}
	}
	return codes
}

function isCatalogAppEnabled(appCode, enabledCodes) {
	const code = String(appCode || '').toUpperCase()
	return CORE_PC_APP_CODES.has(code) || enabledCodes === null || enabledCodes.has(code)
}

function isPcWorkspaceCatalogEnabled(appCode, enabledCodes) {
	const code = String(appCode || '').toUpperCase()
	return CORE_PC_WORKSPACE_CODES.has(code) || enabledCodes === null || enabledCodes.has(code)
}

function catalogAppByCode(catalog) {
	const result = new Map()
	for (const category of catalog || []) {
		for (const app of category?.apps || []) {
			const code = String(app?.appCode || '')
				.trim()
				.toUpperCase()
			if (code && !result.has(code)) result.set(code, app)
		}
	}
	return result
}

function normalizeCapabilityCodeSet(capabilityCodes) {
	if (capabilityCodes === undefined) return null
	return new Set(
		(Array.isArray(capabilityCodes) ? capabilityCodes : [])
			.map((code) =>
				String(code || '')
					.trim()
					.toUpperCase()
			)
			.filter(Boolean)
	)
}

function hasAnyCapability(requiredCodes, ownedCodes) {
	if (ownedCodes === null) return true
	return (requiredCodes || []).some((code) =>
		ownedCodes.has(
			String(code || '')
				.trim()
				.toUpperCase()
		)
	)
}

function hasVisibleRoute(route, visiblePaths, operatorOnly, ownedCodes) {
	return (
		visiblePaths.has(normalizePcRoutePath(route)) ||
		(Boolean(operatorOnly) && ownedCodes?.has(PLATFORM_OPERATIONS_CAPABILITY))
	)
}

export function resolvePcAppCategories(catalog, menus = [], capabilityCodes) {
	const visiblePaths = collectVisibleMenuPaths(menus)
	const enabledCodes = collectEnabledCatalogAppCodes(catalog)
	const ownedCodes = normalizeCapabilityCodeSet(capabilityCodes)
	const catalogByCode = new Map()
	for (const category of catalog || []) {
		for (const app of category?.apps || []) {
			const code = String(app?.appCode || '')
				.trim()
				.toUpperCase()
			if (code && !catalogByCode.has(code)) catalogByCode.set(code, { app, category })
		}
	}

	const grouped = new Map()
	for (const entry of PC_APP_REGISTRY) {
		if (!visiblePaths.has(normalizePcRoutePath(entry.route))) continue
		if (!isCatalogAppEnabled(entry.appCode, enabledCodes)) continue
		if (!hasAnyCapability(entry.routeCapabilityCodes, ownedCodes)) continue
		const source = catalogByCode.get(entry.appCode)
		const categoryName = source?.category?.categoryName || entry.category
		const apps = grouped.get(categoryName) || []
		apps.push({
			...entry,
			name: source?.app?.appName || entry.name,
			description: source?.app?.appDescription || '',
			sortOrder: Number(source?.app?.sortOrder || 0)
		})
		grouped.set(categoryName, apps)
	}

	return Array.from(grouped, ([name, apps]) => ({
		name,
		apps: apps.sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name, 'zh-CN'))
	})).filter((category) => category.apps.length > 0)
}

function resolvePcWorkspacesWithCatalog(menus = [], catalog, capabilityCodes, pcCatalog) {
	const visiblePaths = collectVisibleMenuPaths(menus)
	const enabledCodes = collectEnabledCatalogAppCodes(catalog)
	const pcEnabledCodes = collectEnabledCatalogAppCodes(pcCatalog)
	const ownedCodes = normalizeCapabilityCodeSet(capabilityCodes)
	const appByCode = new Map(PC_APP_REGISTRY.map((app) => [app.appCode, app]))
	const pcByCode = catalogAppByCode(pcCatalog)

	return PC_WORKSPACE_REGISTRY.map((workspace) => {
		if (!isPcWorkspaceCatalogEnabled(workspace.appCode, pcEnabledCodes)) return null
		const catalogCodesForRoute = (route) => route.appCodes
		const features = workspace.appCodes
			.map((appCode) => appByCode.get(appCode))
			.filter(
				(feature) =>
					feature &&
						hasVisibleRoute(feature.route, visiblePaths, feature.operatorOnly || workspace.operatorOnly, ownedCodes) &&
					isCatalogAppEnabled(feature.appCode, enabledCodes) &&
					hasAnyCapability(feature.routeCapabilityCodes, ownedCodes)
			)
			.map((feature) => ({
				...feature,
				capabilityCodes: feature.routeCapabilityCodes,
				scope: feature.scope || workspace.scope || 'store'
			}))
		const extraRoutes = (workspace.extraRoutes || [])
			.filter((item) => {
				const routePath = normalizePcRoutePath(item.route)
				// 结单查询与超级工作台主入口共享同一菜单可见性：只要有预约开单菜单或具备前台能力即可展示，无需单独菜单资源
				const isSettledOrdersRoute = routePath === '/biz/mrfrontdeskorders' && workspace.appCode === 'PC_STORE_OPERATION'
				const visible = isSettledOrdersRoute
					? (visiblePaths.has('/biz/mrfrontdesk') || hasVisibleRoute(item.route, visiblePaths, item.operatorOnly || workspace.operatorOnly, ownedCodes))
					: hasVisibleRoute(item.route, visiblePaths, item.operatorOnly || workspace.operatorOnly, ownedCodes)
				return visible && catalogCodesForRoute(item).some((appCode) => isCatalogAppEnabled(appCode, enabledCodes)) && hasAnyCapability(item.capabilityCodes, ownedCodes)
			})
			.map((item) => ({
				...item,
				appCode: `${workspace.appCode}_${item.route}`,
				capabilityCodes: item.capabilityCodes,
				scope: item.scope || workspace.scope || 'store'
			}))
		// Allow workspaces that only expose extraRoutes (e.g. HR when staff mgmt is capability-only)
		if (enabledCodes !== null && features.length === 0 && extraRoutes.length === 0) return null
		let navigation = workspace.extraRoutesFirst ? [...extraRoutes, ...features] : [...features, ...extraRoutes]
		// 超级工作台：结单查询紧跟预约开单（预约开单 → 结单查询 → 抽奖活动 → 抽奖核销 → 会员中心）
		if (workspace.appCode === 'PC_STORE_OPERATION' && features.length > 0 && extraRoutes.length > 0) {
			const settledRoute = '/biz/mrfrontdeskorders'
			const settled = extraRoutes.find((item) => normalizePcRoutePath(item.route) === settledRoute)
			if (settled) {
				const remainingExtras = extraRoutes.filter((item) => normalizePcRoutePath(item.route) !== settledRoute)
				const [firstFeature, ...restFeatures] = features
				navigation = firstFeature ? [firstFeature, settled, ...restFeatures, ...remainingExtras] : navigation
			}
		}
		const route = navigation.some((item) => pcRouteMatches(item.route, workspace.defaultRoute))
			? workspace.defaultRoute
			: navigation[0]?.route
		if (!route) return null
		const capabilityCodes = [
			...new Set([
				...features.flatMap((feature) => feature.capabilityCodes),
				...extraRoutes.flatMap((item) => item.capabilityCodes || [])
			])
		]
		const configured = pcByCode.get(workspace.appCode)
		return {
			...workspace,
			name: configured?.appName || workspace.name,
			icon: workspace.appCode === 'PC_PLATFORM_OPERATIONS' ? workspace.icon : configured?.appIcon || workspace.icon,
			appBgColor: configured?.appBgColor || workspace.appBgColor,
			description: configured?.appDescription || workspace.description || '',
			desktopOrder: Number(configured?.sortOrder ?? workspace.desktopOrder ?? 0),
			route,
			features,
			navigation,
			// Prefer declared workspace scope; only fall back to active route scope when unset
			scope: workspace.scope || 'store',
			capabilityCodes
		}
	}).filter(Boolean)
}

export function resolvePcWorkspaces(menus = [], catalog, capabilityCodes, pcCatalog) {
	return resolvePcWorkspacesWithCatalog(menus, catalog, capabilityCodes, pcCatalog)
}

/** Home desktop sections: group workspaces by desktopGroup. */
export function resolvePcDesktopSections(menus = [], catalog, capabilityCodes, pcCatalog) {
	const workspaces = resolvePcWorkspacesWithCatalog(menus, catalog, capabilityCodes, pcCatalog).sort(
		(a, b) => (a.desktopOrder || 0) - (b.desktopOrder || 0) || a.name.localeCompare(b.name, 'zh-CN')
	)
	const byGroup = new Map(PC_DESKTOP_GROUPS.map((group) => [group.id, []]))
	for (const workspace of workspaces) {
		const groupId = workspace.desktopGroup || 'ops'
		const list = byGroup.get(groupId) || byGroup.get('ops')
		list.push(workspace)
	}
	return PC_DESKTOP_GROUPS.map((group) => ({
		id: group.id,
		name: group.name,
		description: group.description,
		apps: byGroup.get(group.id) || []
	})).filter((section) => section.apps.length > 0)
}

export function resolveActivePcWorkspace(workspaces = [], currentRoute = '') {
	const workspace =
		workspaces.find((item) =>
			item.navigation.some((navigationItem) => pcRouteMatches(currentRoute, navigationItem.route))
		) || null
	if (!workspace) return null
	const activeNavigation = workspace.navigation.find((item) => pcRouteMatches(currentRoute, item.route))
	return { ...workspace, scope: activeNavigation?.scope || workspace.scope || 'store' }
}

export function resolvePcRouteCapabilityCodes(currentRoute = '') {
	const route = normalizePcRoutePath(currentRoute)
	if (PC_UNGUARDED_ROUTES.includes(route)) return []
	const capabilityCodes = new Set()
	let managed = false

	for (const [targetRoute, routeCodes] of Object.entries(PC_NON_NAVIGATION_ROUTE_CAPABILITIES)) {
		if (!pcRouteMatches(route, targetRoute)) continue
		managed = true
		routeCodes.forEach((code) => capabilityCodes.add(code))
	}

	for (const app of PC_APP_REGISTRY) {
		if (!pcRouteMatches(route, app.route)) continue
		managed = true
		app.routeCapabilityCodes.forEach((code) => capabilityCodes.add(code))
	}

	for (const workspace of PC_WORKSPACE_REGISTRY) {
		const matchedRoute = [
			{
				route: workspace.defaultRoute,
				capabilityCodes: workspace.defaultCapabilityCodes
			},
			...(workspace.extraRoutes || [])
		].find((item) => pcRouteMatches(route, item.route))
		if (!matchedRoute) continue
		managed = true
		const routeCodes = matchedRoute.capabilityCodes
		routeCodes.forEach((appCode) => capabilityCodes.add(appCode))
	}

	return managed ? [...capabilityCodes] : null
}

export function hasPcRouteCapability(currentRoute = '', capabilityCodes = []) {
	const requiredCodes = resolvePcRouteCapabilityCodes(currentRoute)
	if (requiredCodes === null) return false
	if (requiredCodes.length === 0) return true
	const ownedCodes = new Set(
		(capabilityCodes || []).map((code) =>
			String(code || '')
				.trim()
				.toUpperCase()
		)
	)
	return requiredCodes.some((code) =>
		ownedCodes.has(
			String(code || '')
				.trim()
				.toUpperCase()
		)
	)
}

export function isPcRouteEnabled(currentRoute = '', catalog, pcCatalog) {
	const requiredCodes = resolvePcRouteCapabilityCodes(currentRoute)
	if (requiredCodes === null) return false
	if (requiredCodes.length === 0) return true
	const enabledCodes = collectEnabledCatalogAppCodes(catalog)
	const pcEnabledCodes = collectEnabledCatalogAppCodes(pcCatalog)
	const managedWorkspace = PC_WORKSPACE_REGISTRY.find((workspace) => {
		if (pcRouteMatches(currentRoute, workspace.defaultRoute)) return true
		if ((workspace.extraRoutes || []).some((item) => pcRouteMatches(currentRoute, item.route))) return true
		if (workspace.appCodes.some((appCode) => pcRouteMatches(currentRoute, PC_APP_REGISTRY.find((app) => app.appCode === appCode)?.route))) {
			return true
		}
		return Object.entries(PC_NON_NAVIGATION_ROUTE_CAPABILITIES).some(
			([route, codes]) =>
				pcRouteMatches(currentRoute, route) &&
				codes.some(
					(code) =>
						workspace.appCodes.includes(code) ||
						(workspace.extraRoutes || []).some((item) => (item.appCodes || []).includes(code))
				)
		)
	})
	if (managedWorkspace && !isPcWorkspaceCatalogEnabled(managedWorkspace.appCode, pcEnabledCodes)) return false
	const declaredAppCodes = requiredCodes.filter((code) => String(code).toUpperCase().startsWith('BM_'))
	if (declaredAppCodes.length) return declaredAppCodes.some((appCode) => isCatalogAppEnabled(appCode, enabledCodes))
	const owningAppCodes = PC_APP_REGISTRY.filter((app) => pcRouteMatches(currentRoute, app.route)).map(
		(app) => app.appCode
	)
	if (owningAppCodes.some((appCode) => isCatalogAppEnabled(appCode, enabledCodes))) return true
	// Leaf permissions protect the API/menu; each extra route declares its owning app.
	return PC_WORKSPACE_REGISTRY.some((workspace) => {
		const matchedRoute = (workspace.extraRoutes || []).find((item) => pcRouteMatches(currentRoute, item.route))
		return Boolean(matchedRoute) && matchedRoute.appCodes.some((appCode) => isCatalogAppEnabled(appCode, enabledCodes))
	})
}

export function isPcBusinessRoute(currentRoute = '') {
	const route = normalizePcRoutePath(currentRoute)
	return route === '/dashboard' || route.startsWith('/biz/')
}

export function paginatePcDesktopApps(apps = [], columns = 8, includeWidget = false) {
	const columnCount = Math.max(1, Number(columns) || 8)
	const pageCapacity = columnCount * 4
	const remaining = [...apps]
	const pages = []

	while (remaining.length > pageCapacity) pages.push(remaining.splice(0, pageCapacity))
	pages.push(remaining)
	if (includeWidget && pages[pages.length - 1].length > pageCapacity - 4) pages.push([])

	return pages.map((pageApps, pageIndex) => {
		const items = pageApps.map((app) => ({ type: 'app', key: app.appCode, app }))
		if (!includeWidget || pageIndex !== pages.length - 1) return items
		const widgetIndex =
			columnCount >= 10 ? Math.min(columnCount * 3 + 2, items.length) : Math.min(columnCount, items.length)
		items.splice(widgetIndex, 0, { type: 'widget', key: 'app-overview' })
		return items
	})
}
